import { Tabs, Card, Spin, Alert, Button, Space } from "antd";
import type { TabsProps } from "antd";
import SearchByName from "../search-by-name";
import SearchByID from "../search-by-id";
import EASSearchEmpty from "../eas-search-empty";
import EASSearchResultCard from "../eas-search-result-card";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { useEffect, useState } from "react";
import { isEmpty } from "lodash";
import { useTranslation } from "react-i18next";
import { resetEASResult, setSearchID, setSearchName } from "redux/reducers/EASSlice";
import UploadDocModal from "pages/school-user/eas-applications/upload-doc-modal";
import styles from "./styles.module.scss";
import { useHistory } from "react-router-dom";

interface EASSearchCard {
  desktop?: boolean;
  redirect?: boolean;
}

const activeStyle = {
  color: "#394049",
  fontWeight: 500
};
const InactiveStyle = {
  color: "#000000D9",
  fontWeight: 400
};

export default function EASSearchCard({ desktop = false, redirect = false }: EASSearchCard) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [searched, setSearched] = useState(false);
  const [isEISModalOpen, setIsEISModalOpen] = useState<boolean>(false);
  const [selectedStudent, setSelectedStudent] = useState<any>();
  const { isLoading, isLoadingError, data } = useAppSelector(state => state.eas);
  const { isEasOpen } = useAppSelector(state => state.properties.closingData);
  const [activeTab, setActiveTab] = useState("1");
  const history = useHistory<{ activeTab?: string }>();

  useEffect(() => {
    if (history.location.state) {
      const previousActiveKey = history.location.state.activeTab as string;
      setActiveTab(previousActiveKey);
    }
    return () => {
      if (!redirect) {
        resetEASData();
      }
    };
  }, []);

  useEffect(() => {
    if (isLoading) {
      if (!searched) {
        setSearched(true);
      }
    }
  }, [isLoading]);

  const resetEASData = () => {
    dispatch(resetEASResult([]));
    dispatch(setSearchID(undefined));
    dispatch(setSearchName(undefined));
  };

  const clearSearchAndData = () => {
    setActiveTab("1");
    setSearched(false);
    resetEASData();
  };

  const items: TabsProps["items"] = [
    {
      key: "1",
      label: (
        <div style={activeTab === "1" ? activeStyle : InactiveStyle}>
          <span className={styles["tab-font"]}>{t("easApplications.search.name.title")}</span>
        </div>
      ),
      children: <SearchByName desktop={desktop} redirect={redirect} activeTab={activeTab} />
    },
    {
      key: "2",
      label: (
        <div style={activeTab === "2" ? activeStyle : InactiveStyle}>
          <span className={styles["tab-font"]}>{t("easApplications.search.id.title")}</span>
        </div>
      ),
      children: <SearchByID desktop={desktop} redirect={redirect} activeTab={activeTab} />
    }
  ];

  return !isEasOpen ? (
    <Card title={<h2 className="card-title">{t("dashboard.card-4.title")}</h2>}>
      <Alert message={t("dashboard.card-4.closed")} type="warning" showIcon />
    </Card>
  ) : (
    <div>
      <UploadDocModal
        isopen={isEISModalOpen}
        onCloseModal={() => setIsEISModalOpen(false)}
        student={selectedStudent}
      />
      <Card bodyStyle={{ padding: 0 }}>
        {redirect && (
          <h2 className="card-title" style={{ padding: "16px 24px" }}>
            {t("dashboard.card-4.title")}
          </h2>
        )}
        <Tabs
          items={items}
          activeKey={activeTab}
          tabBarStyle={{ padding: "0px 24px" }}
          onChange={key => {
            setActiveTab(key);
          }}
        />
      </Card>
      {!isLoading && searched && isEmpty(data) && (
        <Card style={{ marginBottom: 16, marginTop: 16 }}>
          <EASSearchEmpty onClear={() => clearSearchAndData()} />
        </Card>
      )}
      {isLoading && <Spin size="large" className="space-above-large center-content" />}
      {!isLoading && !isLoadingError && !isEmpty(data) && (
        <div>
          <div className={styles["result-container"]}>
            {data?.map(item => (
              <EASSearchResultCard
                key={item.studentNumber}
                easResult={item}
                setIsEISModalOpen={setIsEISModalOpen}
                setSelectedStudent={setSelectedStudent}
              />
            ))}
          </div>
          <Space direction="vertical" align="center" style={{ width: "100%" }}>
            <Button
              type="link"
              className="space-above-large center-content"
              onClick={clearSearchAndData}
            >
              {t("easApplications.search.clear")}
            </Button>
          </Space>
        </div>
      )}
    </div>
  );
}
